import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiGlobe, FiCheck } from "react-icons/fi";
import AppChip from "../ui/AppChip";
import { useLanguage } from "../../i18n";
import { SHEET_CLOSE_MS } from "../ui/sheetConstants";
import "./CountrySelectorSheet.css";

export default function CountrySelectorSheet({
  open,
  onClose,
  countries = [],
  languages = [],
  selectedCountry = "",
  selectedLanguage = "en",
  onSelectCountry,
  onSelectLanguage,
}) {
  const { t } = useLanguage();
  const onCloseRef = useRef(onClose);
  const isClosingRef = useRef(false);

  const [shouldRender, setShouldRender] = useState(open);
  const [isAnimatedIn, setIsAnimatedIn] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  onCloseRef.current = onClose;

  const finishClose = useCallback(() => {
    isClosingRef.current = false;
    setIsClosing(false);
    setShouldRender(false);
    setIsAnimatedIn(false);
    onCloseRef.current?.();
  }, []);

  const requestClose = useCallback(() => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    setIsClosing(true);
    setIsAnimatedIn(false);
    window.setTimeout(finishClose, SHEET_CLOSE_MS);
  }, [finishClose]);

  useEffect(() => {
    if (!open) return undefined;

    setShouldRender(true);
    isClosingRef.current = false;
    setIsClosing(false);

    const enterFrame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        setIsAnimatedIn(true);
      });
    });

    return () => {
      window.cancelAnimationFrame(enterFrame);
    };
  }, [open]);

  useEffect(() => {
    if (open || !shouldRender) return undefined;
    if (!isClosingRef.current) {
      requestClose();
    }
    return undefined;
  }, [open, shouldRender, requestClose]);

  useEffect(() => {
    if (!shouldRender) return undefined;

    const scrollY = window.scrollY;
    const { style } = document.body;
    const prevOverflow = style.overflow;
    const prevPosition = style.position;
    const prevTop = style.top;
    const prevWidth = style.width;

    style.overflow = "hidden";
    style.position = "fixed";
    style.top = `-${scrollY}px`;
    style.width = "100%";

    const onKey = (e) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKey);

    return () => {
      style.overflow = prevOverflow;
      style.position = prevPosition;
      style.top = prevTop;
      style.width = prevWidth;
      window.scrollTo(0, scrollY);
      window.removeEventListener("keydown", onKey);
    };
  }, [shouldRender, requestClose]);

  if (!shouldRender) return null;

  const rootClass = [
    "country-sheet-root",
    isAnimatedIn ? "country-sheet-root--open" : "",
    isClosing ? "country-sheet-root--closing" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return createPortal(
    <div className={rootClass} role="presentation">
      <button
        type="button"
        className="country-sheet-backdrop"
        onClick={requestClose}
        aria-label={t("browse.close")}
      />
      <div
        className="country-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={t("browse.title")}
      >
        <div className="country-sheet__handle" aria-hidden />
        <header className="country-sheet__header">
          <div className="country-sheet__title-row">
            <FiGlobe aria-hidden />
            <h2>{t("browse.title")}</h2>
          </div>
          <button
            type="button"
            className="country-sheet__close"
            onClick={requestClose}
            aria-label={t("browse.close")}
          >
            ✕
          </button>
        </header>
        <p className="country-sheet__hint">{t("browse.hint")}</p>
        <div className="country-sheet__scroll">
          <section className="country-sheet__section">
            <h3 className="country-sheet__section-title">{t("browse.language")}</h3>
            <div className="country-sheet__language-track" role="listbox" aria-label={t("browse.language")}>
              {languages.map((language) => (
                <AppChip
                  key={language.code}
                  label={language.nativeLabel}
                  selected={selectedLanguage === language.code}
                  onClick={() => onSelectLanguage?.(language.code)}
                />
              ))}
            </div>
          </section>

          <section className="country-sheet__section">
            <h3 className="country-sheet__section-title">{t("browse.country")}</h3>
            <ul className="country-sheet__list">
              {countries.map((country) => {
                const isSelected = selectedCountry === country.name;
                return (
                  <li key={country.code ?? country.name}>
                    <button
                      type="button"
                      className={`country-sheet__option${isSelected ? " country-sheet__option--selected" : ""}`}
                      onClick={() => {
                        onSelectCountry?.(country.name);
                        requestClose();
                      }}
                    >
                      <span className="country-sheet__option-label">{country.name}</span>
                      {isSelected ? <FiCheck className="country-sheet__check" aria-hidden /> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        </div>
      </div>
    </div>,
    document.body,
  );
}
